import React, { useState, useEffect } from 'react';
import { verifySession, updateThemePreference } from '../services/auth';
import './ThemeToggle.css';
const ThemeToggle: React.FC = () => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    // Verificar si hay una preferencia guardada en localStorage (fallback)
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Si no hay preferencia guardada, usar la preferencia del sistema
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  // Cargar preferencia de tema desde la BD al iniciar (solo si hay sesión activa)
  useEffect(() => {
    const loadThemeFromDB = async () => {
      try {
        const result = await verifySession();
        if (result.success && result.user?.preferredTheme) {
          const themeFromDB = result.user.preferredTheme === 'dark';
          setIsDark(themeFromDB);
          localStorage.setItem('theme', result.user.preferredTheme);
        } else {
          // Si no hay tema en BD, usar localStorage o preferencia del sistema
          const savedTheme = localStorage.getItem('theme');
          if (savedTheme) {
            setIsDark(savedTheme === 'dark');
          }
        }
      } catch (error) {
        // En caso de error (ej: no hay sesión activa), usar localStorage o preferencia del sistema
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
          setIsDark(savedTheme === 'dark');
        }
      }
    };
    loadThemeFromDB();
  }, []);
  useEffect(() => {
    // Aplicar el tema al cargar o cambiar
    if (isDark) {
      document.documentElement.classList.add('dark-theme');
      document.documentElement.classList.remove('light-theme');
    } else {
      document.documentElement.classList.add('light-theme');
      document.documentElement.classList.remove('dark-theme');
    }
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);
  const toggleTheme = async () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    // Guardar en la BD (solo si hay sesión activa)
    try {
      const themeValue = newTheme ? 'dark' : 'light';
      await updateThemePreference(themeValue);
    } catch (error) {
      // Si hay error (ej: no hay sesión activa), solo guardar en localStorage
    }
  };
  return (
    <button
      type="button"
      className="theme-toggle-button"
      onClick={toggleTheme}
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      title={isDark ? 'Modo oscuro activo' : 'Modo claro activo'}
    >
      {isDark ? (
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
      ) : (
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
      )}
    </button>
  );
};
export default ThemeToggle;